/**
 * Sales · new work commissioned - two side-by-side charts (months
 * left, quarters right) off the Schedule of Work COM DATE, each with
 * a dashed same-period-last-year comparison line. Click a bar to see
 * the commissioned projects behind it. MediLAB orders excluded.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, ResponsiveContainer,
  Tooltip as RechartsTooltip, Cell,
} from "recharts";
import { Loader2, X, ExternalLink } from "lucide-react";
import { fmtGbpShort, PanelTitle } from "../staff/CommissionedPanels";
import { InfoTip } from "./cfoShared";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtGbpExact = (v) => `£${Math.round(v).toLocaleString("en-GB")}`;

const fmtMonth = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
};

const fmtQuarter = (q) => `${q.slice(5)} ${q.slice(2, 4)}`;

const fmtLong = (p) =>
  p.includes("Q")
    ? `${p.slice(5)} ${p.slice(0, 4)}`
    : new Date(+p.slice(0, 4), +p.slice(5, 7) - 1, 1)
        .toLocaleDateString("en-GB", { month: "long", year: "numeric" });

function SalesChart({ rows, grain, onDrill, testid }) {
  const chart = rows.map((r) => ({
    ...r,
    period: grain === "month" ? r.month : r.quarter,
    label: grain === "month" ? fmtMonth(r.month) : fmtQuarter(r.quarter),
  }));
  return (
    <div className="h-56" data-testid={testid}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={chart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10, fill: "#a8a29e" }}
            axisLine={false} tickLine={false}
            interval={grain === "month" ? 1 : 0}
          />
          <YAxis
            tickFormatter={(v) => fmtGbpShort(v)}
            tick={{ fontSize: 10, fill: "#a8a29e" }}
            axisLine={false} tickLine={false} width={54}
          />
          <RechartsTooltip
            contentStyle={{ fontSize: 12, borderRadius: 8 }}
            formatter={(v, name) => [
              fmtGbpExact(v),
              name === "value" ? "commissioned" : "same period last year",
            ]}
            labelFormatter={(l, p) => {
              const row = p?.[0]?.payload;
              if (!row) return l;
              let s = fmtLong(row.period) + (row.partial ? " · still filling" : "");
              if (row.top) s += ` · incl. ${row.top.client} ${fmtGbpShort(row.top.value)}`;
              if (row.prev_year_top) s += ` · LY incl. ${row.prev_year_top.client} ${fmtGbpShort(row.prev_year_top.value)}`;
              return s;
            }}
          />
          <Bar dataKey="value" maxBarSize={26} onClick={(e) => onDrill(e?.payload?.period)}>
            {chart.map((r) => (
              <Cell
                key={r.period}
                fill="#1B4F8A"
                fillOpacity={r.partial ? 0.45 : 1}
                radius={[3, 3, 0, 0]}
                style={{ cursor: "pointer" }}
              />
            ))}
          </Bar>
          <Line
            type="monotone"
            dataKey="prev_year_value"
            stroke="#78716c"
            strokeWidth={1.5}
            strokeDasharray="5 3"
            dot={{ r: 2, fill: "#78716c" }}
            connectNulls={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function SalesDrill({ period, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    setData(null);
    setError("");
    axios.get(`${API}/api/proposals/pipeline/sales-commissioned/detail`, { params: { period } })
      .then((r) => { if (live) setData(r.data); })
      .catch((e) => { if (live) setError(e.response?.data?.detail || "Could not load the detail."); });
    return () => { live = false; };
  }, [period]);

  return (
    <div className="fixed inset-0 z-50" data-testid="sales-drill">
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} data-testid="sales-drill-backdrop" />
      <aside className="absolute inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl flex flex-col">
        <header className="px-5 py-4 border-b border-stone-200 flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-wider text-stone-400">Work commissioned</p>
            <h3 className="font-serif text-xl text-slate-900 leading-tight">{fmtLong(period)}</h3>
            {data && (
              <p className="text-xs text-stone-500 mt-1">
                {data.count} project{data.count === 1 ? "" : "s"} ·{" "}
                <span className="font-mono text-[#1B4F8A]">{fmtGbpExact(data.total)}</span>
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} data-testid="sales-drill-close"
            className="p-1.5 rounded-lg text-stone-400 hover:text-slate-800 hover:bg-stone-100 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {!data && !error && (
            <div className="flex items-center gap-2 text-stone-500 text-sm py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {error && <p className="text-sm text-amber-800 py-4">{error}</p>}
          {data && data.items.length === 0 && (
            <p className="text-sm text-stone-500 py-4">Nothing was commissioned in this period.</p>
          )}
          {data && (
            <ul className="divide-y divide-stone-100">
              {data.items.map((i, idx) => (
                <li key={idx} className="py-2.5 flex items-start gap-3" data-testid={`sales-drill-item-${idx}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-slate-800 truncate">
                      {i.client}
                      {i.project_name && <span className="text-stone-400 font-normal"> · {i.project_name}</span>}
                    </p>
                    <p className="text-[10px] font-mono text-stone-400 mt-0.5">
                      {i.com_date} · {i.team}{i.status ? ` · ${i.status}` : ""}
                    </p>
                  </div>
                  <p className="text-xs font-mono tabular-nums text-slate-800 shrink-0">{fmtGbpExact(i.value)}</p>
                  {i.cognito_admin && (
                    <a href={i.cognito_admin} target="_blank" rel="noreferrer"
                      className="mt-0.5 text-stone-300 hover:text-[#1B4F8A] transition-colors shrink-0">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}

export default function SalesCommissioned() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [drill, setDrill] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/proposals/pipeline/sales-commissioned`)
      .then((r) => { setData(r.data); setError(""); })
      .catch((e) => setError(e.response?.data?.detail || "Could not load the sales timeline."));
  }, []);

  if (error) return null;
  if (!data) {
    return (
      <section className="bg-white rounded-2xl border border-stone-200 p-5" data-testid="sales-commissioned">
        <div className="flex items-center gap-2 text-stone-500 text-sm py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading sales…
        </div>
      </section>
    );
  }

  const months = data.months.slice(-13);
  const quarters = data.quarters.slice(-8);

  return (
    <section className="bg-white rounded-2xl border border-stone-200 p-5" data-testid="sales-commissioned">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <PanelTitle>Sales · new work commissioned £ <InfoTip id="ov_sales" /></PanelTitle>
        <div className="flex items-center gap-4 text-[10px] font-mono text-stone-500">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-[#1B4F8A]" /> commissioned
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-4 border-t-2 border-dashed border-stone-500" /> same period last year
          </span>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-wider text-stone-400 mb-1">By month · last 13</p>
          <SalesChart rows={months} grain="month" onDrill={setDrill} testid="sales-chart-month" />
        </div>
        <div>
          <p className="text-[10px] font-mono uppercase tracking-wider text-stone-400 mb-1">By quarter · last 8</p>
          <SalesChart rows={quarters} grain="quarter" onDrill={setDrill} testid="sales-chart-quarter" />
        </div>
      </div>
      <p className="text-[10px] text-stone-400 mt-3" data-testid="sales-commissioned-note">
        Source: the Schedule of Work COM DATE - the date each project was commissioned. Each opportunity
        counts once per period (re-quotes and revisions collapse into their originating quote); the value
        is the turnover quoted on the commissioned version. <strong>Excludes MediLAB orders.</strong> The
        lighter bar is the current period, still filling. Click a bar for the projects behind it.
      </p>
      {drill && <SalesDrill period={drill} onClose={() => setDrill(null)} />}
    </section>
  );
}
